import { noop } from "lodash";
import type { QueryClient } from "react-query";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import { notifications } from "@/react/helpers/urlHelpers/internalApi/notifications";
import type { ApiError } from "@circle-react/config/CustomErrors";
import {
  reactQueryDelete,
  reactQueryGet,
  reactQueryPatch,
  reactQueryPost,
} from "@circle-react/helpers/backendRequestHelpers";
import type {
  NotificationAction,
  NotificationPage,
} from "@circle-react/types/Notification";
import { TABS } from "./NotificationContext";
import { NOTIFICATIONS_PER_PAGE } from "./constants";

export const ACTION_API_MAPPER: any = {
  mark_as_read: ({ id }: any) =>
    reactQueryPatch(notifications.markAsRead({ id })),
  mark_all_as_read: () => reactQueryPost(notifications.markAllAsRead({})),
  follow: ({ postId }: any) => reactQueryPost(notifications.follow({ postId })),
  unfollow: ({ postId, followerId }: any) =>
    reactQueryDelete(notifications.unfollow({ postId, followerId })),
  archive: ({ id }: any) => reactQueryPatch(notifications.archive({ id })),
};

const NOTIFICATION_STALE_TIME = 60 * 1000;

export interface InvalidateNotificationsQueryCache {
  queryClient: QueryClient;
}

export const invalidateNotificationsQueryCache = ({
  queryClient,
}: InvalidateNotificationsQueryCache) => {
  void queryClient.invalidateQueries(["notifications"]);
};

export interface UseFetchNotifications {
  notificationGroup?: string;
  perPage?: number;
}

export const useFetchNotifications = ({
  perPage = NOTIFICATIONS_PER_PAGE,
  notificationGroup = TABS.INBOX,
}: UseFetchNotifications) => {
  const {
    isLoading,
    data: notificationsData,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<NotificationPage>(
    ["notifications", notificationGroup],
    async ({ pageParam = 1 }): Promise<NotificationPage> => {
      const requestParams: any = {
        per_page: String(perPage),
        notification_group: notificationGroup,
      };

      // pageParam is always a page number
      if (pageParam === 1) {
        // First page
        requestParams.page = pageParam;
      } else {
        // For subsequent pages, check if the previous page has a cursor
        const previousPage = notificationsData?.pages[pageParam - 2];
        if (previousPage?.next_search_after) {
          // Use cursor-based pagination
          requestParams.next_search_after = previousPage.next_search_after;
          requestParams.page = pageParam;
        } else {
          // Fall back to page-based pagination
          requestParams.page = pageParam;
        }
      }

      return reactQueryGet(notifications.fetch(requestParams));
    },
    {
      staleTime: NOTIFICATION_STALE_TIME,
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
    },
  );

  const lastPage =
    notificationsData?.pages[notificationsData?.pages.length - 1];
  const hasNextPage = !!lastPage?.has_next_page;

  return {
    isLoading,
    isFetchingNextPage,
    notifications: notificationsData,
    refetch,
    fetchNextPage,
    hasNextPage,
  };
};

export const useNotificationsCount = (
  communityId: any,
  setNotificationTabCounters: any,
) =>
  useQuery(
    ["notifications", "count", communityId],
    () =>
      reactQueryGet(
        notifications.fetchNotificationCount({ community_id: communityId }),
      ),
    {
      enabled: !!communityId,
      onSuccess: ({
        new_notifications_count,
        new_mentions_count,
        new_inbox_count,
      }) => {
        setNotificationTabCounters({
          inbox: new_inbox_count,
          mentions: new_mentions_count,
          notifications: new_notifications_count,
        });
      },
    },
  );

export const useResetNotificationCount = (setNotificationCount: any) => {
  const { mutate } = useMutation(
    () => reactQueryPost(notifications.resetNotificationCount()),
    {
      onSuccess: data => {
        setNotificationCount(data.new_notifications_count);
      },
    },
  );
  return { mutate };
};

export const useNotificationsActions = (
  onSuccess: (data: any) => void = noop,
) => {
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation<
    NotificationAction,
    ApiError,
    NotificationAction
  >(
    ({ action, id, postId, followerId }) =>
      ACTION_API_MAPPER[action]({ id, postId, followerId }),
    {
      onSuccess: data => {
        invalidateNotificationsQueryCache({ queryClient });
        onSuccess(data);
      },
    },
  );

  return { mutate, isLoading };
};
